import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const LAST_UPDATED = "February 5, 2026";

/**
 * Terms of Service — readable, K-12 friendly. Not a license to print legal
 * boilerplate; protects The WE Mentality without being hostile to schools.
 */
export default function TermsPage() {
  useDocumentMeta({
    title: "Terms of Service | Lemonade Stand Business Plan",
    description:
      "Terms of Service for The Lemonade Stand Business Plan — a K-12 entrepreneurship learning platform built by The WE Mentality. Clear, readable, school-friendly.",
    keywords:
      "terms of service, K-12 learning platform, educational technology, entrepreneurship education",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex flex-col">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14 flex-1 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-6"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <header className="mb-8">
          <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-800 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <FileText className="w-3.5 h-3.5" /> User Agreement
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Terms of Service
          </h1>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
        </header>

        <section className="prose prose-sm sm:prose-base max-w-none text-gray-800 leading-relaxed space-y-6">
          <p>
            These Terms of Service ("Terms") are a binding agreement between
            you and <strong>The WE Mentality</strong> (Las Vegas, Nevada),
            operator of the Lemonade Stand Business Plan platform
            ("Platform"). By creating an account or using the Platform, you
            agree to these Terms.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">1. What the Platform is</h2>
          <p>
            The Platform is a K-12 entrepreneurship and career-readiness
            program with three pathways (Elementary, Middle School, High
            School). It includes lessons, an AI coach (LemonBot), a Mini
            Business Plan builder, certificates, and educator tools.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">2. Who can use it</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Educators (18+) and school administrators may create accounts on
              behalf of their students.
            </li>
            <li>
              Parents/guardians (18+) may create accounts on behalf of their
              children.
            </li>
            <li>
              Students under 13 may only use the Platform through accounts
              created by a parent, guardian, or school acting on their behalf.
            </li>
            <li>
              The public Elementary preview (Lemonade Leaders Jr.) requires no
              account.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 pt-2">3. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Use the Platform to harass, bully, or harm anyone.</li>
            <li>Submit content that is hateful, sexual, violent, or illegal.</li>
            <li>
              Try to break, probe, scrape, reverse-engineer, or overload the
              Platform.
            </li>
            <li>Share your account credentials with anyone.</li>
            <li>
              Use LemonBot or any AI feature to bypass your own work — it's a
              coach, not a ghostwriter.
            </li>
            <li>Pretend to be someone else, including impersonating a teacher.</li>
          </ul>
          <p>
            We may suspend or remove accounts that violate these rules.
            Educators and parents may also remove student accounts under their
            supervision.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">4. Your content</h2>
          <p>
            Students own the work they create — Mini Business Plans, pitches,
            workbook reflections. By submitting content, you give us a limited
            license to display it within the Platform (e.g., back to you, to
            your teacher, to your parent if you share). We will not use
            student work for marketing without explicit parent/school written
            permission.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">5. Our content</h2>
          <p>
            Lessons, videos, designs, and brand assets belong to The WE
            Mentality. You may use them inside the Platform; you may not
            republish, resell, or build a competing product on top of them
            without our permission.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">6. AI features</h2>
          <p>
            LemonBot uses third-party AI models (currently OpenAI). It can be
            wrong, biased, or out-of-date. Treat its replies as suggestions,
            not facts. Educators can disable LemonBot for any class.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">7. Pricing &amp; pilots</h2>
          <p>
            Individual student access is free during our launch period. Paid
            school/district plans are quoted directly via contract. Pilot
            schools receive program access and founder support per their
            pilot agreement.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">8. Availability</h2>
          <p>
            We work hard to keep the Platform available 24/7, but we don't
            guarantee uninterrupted service. Planned maintenance is
            communicated in advance to school customers.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">9. Privacy</h2>
          <p>
            Your use of the Platform is also governed by our{" "}
            <Link to="/privacy" className="text-yellow-700 underline">
              Privacy Policy
            </Link>
            .
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">10. Disclaimers</h2>
          <p>
            The Platform is provided "as is." We don't guarantee any specific
            educational outcome, business outcome, or earnings. We are not
            responsible for losses caused by content students post or by
            third-party services we link to.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">11. Termination</h2>
          <p>
            You may stop using the Platform at any time and delete your
            account at{" "}
            <Link to="/delete-account" className="text-yellow-700 underline">
              /delete-account
            </Link>
            . We may suspend or terminate accounts that violate these Terms,
            with reasonable notice when possible.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">12. Changes</h2>
          <p>
            We may update these Terms. We'll email account holders at least 30
            days before material changes take effect. Your continued use after
            that date means you accept the new Terms.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">13. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Nevada, USA,
            without regard to its conflict-of-law principles. Disputes will be
            resolved in the state or federal courts located in Clark County,
            Nevada.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">14. Contact</h2>
          <p>
            <strong>The WE Mentality</strong>
            <br />
            Las Vegas, Nevada, USA
            <br />
            <a href="mailto:hello@thewementality.com" className="text-yellow-700 underline">
              hello@thewementality.com
            </a>
            {" · "}
            <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
              privacy@thewementality.com
            </a>
          </p>
        </section>
      </div>
      <Footer />
    </div>
  );
}
